import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, computed, inject, signal } from '@angular/core';
import { LUCIDE_ICONS, LucideAngularModule, LucideIconData, LucideIconProvider } from 'lucide-angular';

/** Icônes inconnues déjà signalées (un seul avertissement par nom). */
const warnedUnknownIcons = new Set<string>();

/**
 * Taille sémantique d'une icône (respecte `--icon-sm | --icon-md | --icon-lg`).
 */
export type AppIconSize = 'sm' | 'md' | 'lg';

/**
 * Couleur sémantique (par défaut : `currentColor` pour hériter du contexte).
 */
export type AppIconColor = 'default' | 'muted' | 'primary' | 'danger' | 'success' | 'warning';

/**
 * Wrapper standalone autour de `<lucide-icon>` appliquant la charte du design system.
 *
 * Usage :
 * ```html
 *   <app-icon name="user" />                               <!-- 20px, couleur héritée -->
 *   <app-icon name="trash-2" size="sm" color="danger" />   <!-- 16px, rouge  -->
 *   <app-icon name="plus" size="lg" />                     <!-- 28px -->
 * ```
 *
 * Voir {@link EMOJI_TO_LUCIDE} pour le dictionnaire de correspondance
 * avec les emojis historiques.
 */
@Component({
  selector: 'app-icon',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="icon"
          [class.icon--sm]="size === 'sm'"
          [class.icon--md]="size === 'md'"
          [class.icon--lg]="size === 'lg'"
          [class.icon--muted]="color === 'muted'"
          [class.icon--primary]="color === 'primary'"
          [class.icon--danger]="color === 'danger'"
          [class.icon--success]="color === 'success'"
          [class.icon--warning]="color === 'warning'"
          [attr.aria-hidden]="ariaLabel ? null : true"
          [attr.aria-label]="ariaLabel"
          [attr.role]="ariaLabel ? 'img' : null">
      <lucide-icon *ngIf="isKnown(name)" [name]="name"></lucide-icon>
    </span>
  `,
  styles: [`:host { display: inline-flex; line-height: 0; }`]
})
export class AppIconComponent {
  /** Nom Lucide (ex: "user", "trash-2", "clipboard-list"). */
  @Input({ required: true }) name!: string;

  /** Taille sémantique — défaut 20 px. */
  @Input() size: AppIconSize = 'md';

  /** Couleur sémantique — défaut : héritée du parent (`currentColor`). */
  @Input() color: AppIconColor = 'default';

  /**
   * Libellé accessible si l'icône porte un sens sans texte associé.
   * Si non fourni, l'icône est considérée décorative (`aria-hidden`).
   */
  @Input() ariaLabel?: string;

  private readonly iconProviders = inject(LUCIDE_ICONS, { optional: true }) as LucideIconProvider[] | LucideIconProvider | null;

  /**
   * Vrai si l'icône est enregistrée (main.ts). Une icône absente fait lever une
   * exception à <lucide-icon> pendant la détection de changement, ce qui
   * interrompt le rendu de toute la suite de la page (formulaire affiché
   * « par morceaux ») : on n'affiche alors rien et on avertit en console.
   */
  isKnown(name: string): boolean {
    const pascal = (name ?? '').replace(/(^|-)([a-z0-9])/g, (_, __, c: string) => c.toUpperCase());
    const providers = Array.isArray(this.iconProviders) ? this.iconProviders : this.iconProviders ? [this.iconProviders] : [];
    if (providers.some(p => p.hasIcon(pascal))) return true;
    if (!warnedUnknownIcons.has(name)) {
      warnedUnknownIcons.add(name);
      console.warn(`[app-icon] Icône "${name}" non enregistrée dans main.ts (LucideAngularModule.pick).`);
    }
    return false;
  }
}
